import { useEffect, useLayoutEffect, useRef } from "react"
import { animate, useMotionValue } from "framer-motion"
import { haptic } from "../utils/haptics.utils"

// ──────────────────────────────────────────────────────────────────────────
// TIRARE PER CAMBIARE SETTIMANA
//
// Nella settimana, sul telefono, arrivati in cima si continua a tirare giù
// e si va alla settimana prima; arrivati in fondo si continua a tirare su e
// si va a quella dopo. Lo scorrimento non si ferma al bordo della
// settimana: prosegue nell'altra.
//
// Oltre il bordo la pagina segue il dito sempre meno, come un elastico, e
// nello spazio che si apre un cerchio si riempie (vedi PullIndicator in
// PhoneLessons). Pieno il cerchio, alzando il dito si cambia settimana;
// prima, la pagina torna al suo posto e non succede niente. La settimana
// accanto non si legge prima del tempo: si apre solo quando ci si va.
//
// Il gesto è di un dito solo: con due è un pizzico (usePinchToSwitchView).
// ──────────────────────────────────────────────────────────────────────────

// Quanto bisogna tirare (in pixel di pagina, dopo l'elastico) per cambiare
// settimana: circa un dito e mezzo di strada
export const PULL_THRESHOLD = 90
// oltre questo la pagina non va, per quanto si tiri
const PULL_MAX = 170

// L'elastico: da quanto si è mosso il dito a quanto si muove la pagina
export const rubberBand = distance => PULL_MAX * (1 - Math.exp(-Math.max(0, distance) / 200))

// Dove porta una pagina tirata di `pull` (in giù positivo): -1 la settimana
// prima, 1 quella dopo, 0 da nessuna parte
export const pullDirection = pull => (pull >= PULL_THRESHOLD ? -1 : pull <= -PULL_THRESHOLD ? 1 : 0)

// un pixel o due di tolleranza: il telefono arrotonda lo scorrimento
const isAtTop = () => window.scrollY <= 1
const isAtBottom = () =>
	window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2

// Restituisce di quanto è tirata la pagina (`pull`, in giù positivo) e
// quanto è già andata via quella di prima (`leaving`, da 0 a 1). Tirato
// abbastanza, la pagina scivola via nel verso del dito e `onCommit` riceve
// dove andare; quando la settimana è cambiata (`week`), si riparte da capo.
const usePullToChangeWeek = ({ enabled, week, onCommit }) => {
	const pull = useMotionValue(0)
	const leaving = useMotionValue(0)
	const onCommitRef = useRef(onCommit)
	onCommitRef.current = onCommit
	// mentre la settimana se ne va non si tira di nuovo
	const isCommitting = useRef(false)

	useLayoutEffect(() => {
		isCommitting.current = false
		pull.jump(0)
		leaving.jump(0)
	}, [week, pull, leaving])

	useEffect(() => {
		if (!enabled) return

		// Il browser, oltre il bordo, ha i suoi gesti: su Android tirare giù in
		// cima ricarica la pagina, sull'iPhone la pagina rimbalza. Qui il
		// bordo è nostro.
		const root = document.documentElement
		const overscroll = root.style.overscrollBehaviorY
		root.style.overscrollBehaviorY = "none"

		let touch = null
		const settle = () => animate(pull, 0, { type: "spring", stiffness: 500, damping: 45 })

		const start = event => {
			if (touch?.side) settle()
			touch = null
			// con due dita è un pizzico
			if (event.touches.length !== 1 || isCommitting.current) return
			const { clientX, clientY } = event.touches[0]
			pull.stop()
			touch = { startX: clientX, startY: clientY, lastY: clientY, origin: 0, side: 0, armed: false }
		}

		const move = event => {
			if (!touch) return
			if (event.touches.length !== 1) {
				if (touch.side) settle()
				touch = null
				return
			}
			const { clientX, clientY } = event.touches[0]
			// Si comincia a tirare quando, al bordo, il dito va oltre: da lì in
			// poi conta quanto si è mosso. Un gesto di lato (lo swipe sulle
			// lezioni) non tira.
			if (!touch.side) {
				const isVertical = Math.abs(clientY - touch.startY) > Math.abs(clientX - touch.startX)
				if (isVertical && clientY > touch.lastY && isAtTop()) touch.side = -1
				else if (isVertical && clientY < touch.lastY && isAtBottom()) touch.side = 1
				touch.origin = touch.lastY
			}
			touch.lastY = clientY
			if (!touch.side) return

			// Tornando indietro col dito la pagina torna con lui, e passato il
			// bordo scorre di nuovo da sé. Se il browser l'ha già fatta scorrere
			// (non sempre si può fermarlo), la si lascia a lui.
			const distance = (touch.origin - clientY) * touch.side
			const scrolledAway = touch.side < 0 ? !isAtTop() : !isAtBottom()
			if (distance <= 0 || scrolledAway) {
				if (scrolledAway) settle()
				else pull.set(0)
				touch.side = 0
				touch.armed = false
				return
			}
			if (event.cancelable) event.preventDefault()
			const value = -touch.side * rubberBand(distance)
			pull.set(value)
			// un tick quando il cerchio si riempie: da lì, alzando il dito si va
			const armed = pullDirection(value) !== 0
			if (armed && !touch.armed) haptic("tick")
			touch.armed = armed
		}

		const end = event => {
			if (!touch || event.touches.length > 0) return
			const direction = touch.side ? pullDirection(pull.get()) : 0
			const wasPulling = Boolean(touch.side)
			touch = null
			if (!direction) {
				if (wasPulling) settle()
				return
			}
			// la settimana scivola via nel verso del dito, e arriva l'altra
			isCommitting.current = true
			const transition = { duration: 0.2, ease: "easeIn" }
			animate(leaving, 1, transition)
			animate(pull, pull.get() - direction * 120, transition).then(() =>
				onCommitRef.current?.(direction),
			)
		}

		// Un gesto interrotto (una chiamata, un gesto del sistema) non è un dito
		// che si alza: la pagina torna al suo posto
		const cancel = () => {
			if (touch?.side) settle()
			touch = null
		}

		const passive = { passive: true }
		// non passivo: oltre il bordo il dito muove la pagina, non il browser
		const active = { passive: false }
		document.addEventListener("touchstart", start, passive)
		document.addEventListener("touchmove", move, active)
		document.addEventListener("touchend", end, passive)
		document.addEventListener("touchcancel", cancel, passive)
		return () => {
			document.removeEventListener("touchstart", start, passive)
			document.removeEventListener("touchmove", move, active)
			document.removeEventListener("touchend", end, passive)
			document.removeEventListener("touchcancel", cancel, passive)
			root.style.overscrollBehaviorY = overscroll ?? ""
			if (touch?.side) settle()
		}
	}, [enabled, pull, leaving])

	return { pull, leaving }
}

export default usePullToChangeWeek
